import { Check, FileText } from 'lucide-react';

import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';

import type { LearnBasicsDocumentOption } from './useLearnBasicsDocumentOptions';

interface LearnBasicsDocumentPickerProps {
  options: LearnBasicsDocumentOption[];
  /** Indexes of the currently-checked documents. */
  selected: number[];
  onToggle: (index: number, slug: string) => void;
  /**
   * Lock the picker — used while the note is still generating, since a document
   * is built from that note and there is nothing to build from yet.
   */
  disabled?: boolean;
}

/**
 * Multi-select document picker on the Learn-the-basics Document step. Each
 * checked document opens its own Document tab in the preview; unchecking closes
 * it. Checkboxes (not single-select) so several documents can be generated.
 */
export function LearnBasicsDocumentPicker({
  options,
  selected,
  onToggle,
  disabled = false,
}: LearnBasicsDocumentPickerProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2 transition-opacity',
        disabled && 'pointer-events-none opacity-50'
      )}
    >
      {options.map((option, index) => {
        const isChecked = selected.includes(index);
        return (
          <button
            key={option.slug}
            type="button"
            role="checkbox"
            aria-checked={isChecked}
            disabled={disabled}
            onClick={() => onToggle(index, option.slug)}
            className={cn(
              'flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors',
              isChecked
                ? 'border-accent-forest/40 bg-accent-forest/[0.06]'
                : 'border-border bg-surface hover:border-accent-forest/40 hover:bg-accent-forest/[0.04]'
            )}
          >
            <span
              className={cn(
                'flex size-5 shrink-0 items-center justify-center rounded-md border',
                isChecked
                  ? 'border-accent-forest bg-accent-forest text-text-primary-invert'
                  : 'border-border-strong/40'
              )}
            >
              {isChecked && <Check className="size-3.5" />}
            </span>
            <FileText className="size-4 shrink-0 text-accent-forest" />
            <div className="flex flex-1 flex-col">
              <TypographyP2 className="font-medium text-text-primary">
                {option.name}
              </TypographyP2>
              <TypographyCaption className="text-text-tertiary">
                {option.helper}
              </TypographyCaption>
            </div>
          </button>
        );
      })}
    </div>
  );
}
